import AutoCompleteTextInput from '@/components/form-inputs/AutoCompleteTextInput';
import '@/components/program/travel-customization.styles.css';
import { TRAVEL_PROGRAM_FORM } from '@/constants/forms';
import { RADIUS_OPTIONS } from '@/constants/travel-program';
import FooterContent from '@/modules/account/components/travel-customization/FooterContent';
import HeaderContent from '@/modules/account/components/travel-customization/HeaderContent';
import { isEmpty } from 'lodash';
import type { DialogProps } from 'primereact/dialog';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { MdArrowBack } from 'react-icons/md';
import SecondaryModal from './SecondaryModal';
import { InputController } from '../inputs/InputController';
import DropdownInput from '../inputs/DropdownInput';
import TextInput from '../inputs/TextInput';

interface ModalProps extends DialogProps {
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddLocationModal = (props: ModalProps) => {
	const { setVisible, ...rest } = props;

	const [items, setItems] = useState<string[]>([]);
	const {
		control,
		resetField,
		trigger,
		watch,
		formState: { errors },
	} = useFormContext();
	const hasAddress =
		!isEmpty(watch('locationAddress')) &&
		watch('locationRadius') > 0 &&
		watch('locationName');

	const search = (event: { query: string }) => {
		setItems([...Array(10).keys()].map((item) => `${event.query}-${item}`));
	};

	const handleOnCancel = useCallback(() => {
		resetField('locationName');
		resetField('locationAddress');
		resetField('locationRadius');
		setVisible(false);
	}, [resetField, setVisible]);

	const handleOnSave = useCallback(() => {
		trigger(['locationName', 'locationAddress', 'locationRadius']).then(
			() => {
				if (
					!errors.locationName &&
					!errors.locationAddress &&
					!errors.locationRadius
				) {
					setVisible(false);
				}
			},
		);
	}, [trigger, errors, setVisible]);

	const handleOnHide = () => {
		resetField('locationName');
		resetField('locationAddress');
		resetField('locationRadius');
		setVisible(false);
	};

	return (
		<SecondaryModal
			{...rest}
			className="w-rem-40"
			draggable={false}
			header={
				<HeaderContent
					label="Add Location"
					icon={
						<MdArrowBack
							size={30}
							className="text-primary"
							onClick={() => setVisible(false)}
						/>
					}
				/>
			}
			footer={
				<FooterContent
					formName={TRAVEL_PROGRAM_FORM}
					id="add-location"
					onClickCancel={handleOnCancel}
					onClickSave={handleOnSave}
					saveLabel="Add Location"
				/>
			}
			onHide={handleOnHide}
		>
			<div className="space-y-6">
				<InputController id="locationName" control={control}>
					<TextInput label="Location Name" placeholder="Enter location name" />
				</InputController>

				<AutoCompleteTextInput
					completeMethod={search}
					control={control}
					id="locationAddress"
					formName={TRAVEL_PROGRAM_FORM}
					label="Address"
					pt={{
						root: {
							className: 'w-full',
						},
						input: {
							root: {
								className:
									'w-full tc-dropdown tc-dropdown-content tc-autocomplete-input',
							},
						},
						item: {
							className: 'tc-dropdown-item',
						},
					}}
					suggestions={items}
				/>
				{hasAddress && (
					<div>
						<img
							src={'/images/svg/Locations-1.svg'}
							alt="location"
							width={600}
							height={200}
							className="w-full"
						/>
					</div>
				)}
				{/* TODO not sure about right styles. didnt find this control on figma design */}
				<InputController id="locationRadius" control={control}>
					<DropdownInput
						options={RADIUS_OPTIONS}
						label="Radius"
						placeholder="Select radius"
					/>
				</InputController>
				{/* <DropdownInput
					className="w-full default-dropdown"
					control={control}
					formName={TRAVEL_PROGRAM_FORM}
					id="locationRadius"
					label="Radius"
					options={RADIUS_OPTIONS}
					placeholder="Select radius"
					pt={{
						root: { className: 'tc-dropdown' },
						input: { className: 'tc-dropdown-content' },
						item: { className: 'tc-dropdown-content' },
					}}
				/> */}
				{!errors.locationRadius &&
					!errors.locationAddress &&
					!errors.locationName && (
						<small
							id="radius-help"
							className="ml-4 text-body-xx-small text-onSurfaceVariant"
						>
							Rides can only be requested within XX kms of the
							address you enter.
						</small>
					)}
			</div>
		</SecondaryModal>
	);
};

export default AddLocationModal;
